'use client';

/**
 * MultiAccountTicket — basket order pad. Click Buy/Sell on an instrument and get
 * every account that can trade it (segment-enabled), each with live available
 * margin + a per-account qty input + an estimated-required / sufficiency chip;
 * order settings (type/price/product/validity) on the right.
 *
 * Place fires ONE manual order per participating account via /manual-order. Each
 * is gated SERVER-SIDE by the per-account manual-arm (+ global flag + hard-freeze);
 * an unarmed account returns 409 ("refused"), shown per-leg. Required margin is an
 * ESTIMATE (the broker exposes no pre-trade margin calc).
 */
import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Check, Loader2 } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Input, Textarea } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Money } from '@/components/ui/money';
import { getEligibleAccounts, type EligibleAccount } from '@/lib/api/admin-eligible-accounts';
import { getFunds } from '@/lib/api/admin-funds';
import { placeManualOrder } from '@/lib/api/admin-manual-trading';
import { useLiveQuotes } from '@/lib/streaming/use-live-quotes';
import type { LiveQuote } from '@/lib/streaming/use-live-quote';
import type { Side } from '@/components/trade/order-ticket';
import { cn } from '@/lib/util/cn';

const sel = 'w-full rounded border border-border bg-surface px-2 py-1.5 text-sm';

export interface TicketRef {
  id: string;
  symbol: string;
  segment_id: string;
}

export function MultiAccountTicket({
  open, onOpenChange, instrument, side, quote,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  instrument: TicketRef | null;
  side: Side;
  quote: LiveQuote | null;
}) {
  const eligQ = useQuery({
    queryKey: ['eligible-accounts', instrument?.id],
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- enabled: !!instrument guards queryFn
    queryFn: ({ signal }) => getEligibleAccounts(instrument!.id, signal),
    enabled: open && !!instrument,
  });
  const elig = eligQ.data?.data ?? null;

  const [orderType, setOrderType] = React.useState<'MARKET' | 'LIMIT'>('MARKET');
  const [price, setPrice] = React.useState('');
  const [product, setProduct] = React.useState<'NRML' | 'CNC'>('NRML');
  const [validity, setValidity] = React.useState<'DAY' | 'IOC'>('DAY');
  const [qtys, setQtys] = React.useState<Record<string, string>>({});

  React.useEffect(() => {
    if (!open) {return;}
    setOrderType('MARKET'); setPrice(''); setProduct('NRML'); setValidity('DAY'); setQtys({});
    // reset when a fresh ticket opens
     
  }, [open, instrument?.id, side]);

  // Self-subscribe to THIS instrument's LIVE quote so the LTP + the est-required
  // margin track the market in real time. The option-chain entry point doesn't share
  // the trade page's subscription, so the passed `quote` can be a stale snapshot —
  // prefer the live one, fall back to the prop while the socket warms up.
  const liveQ = useLiveQuotes(instrument ? [instrument.id] : [], 'touchline');
  const q = (instrument ? liveQ.quotes[instrument.id] : null) ?? quote;

  const isBuy = side === 'BUY';
  // A MARKET order crosses the spread: a BUY fills at the ASK, a SELL at the BID.
  // Use THAT side's price for the margin estimate (and the paper fill) — LTP is
  // the last print, not what you'd actually pay. Fall back to LTP when the book
  // side is missing (illiquid). LIMIT uses the entered limit price.
  const marketRef = isBuy ? (q?.ask ?? q?.ltp ?? 0) : (q?.bid ?? q?.ltp ?? 0);
  const refPrice = orderType === 'LIMIT' ? Number(price) || 0 : marketRef;
  const limitInvalid = orderType === 'LIMIT' && !(Number(price) > 0);
  const mult = elig?.contract_multiplier ?? 1;
  const factor = elig?.margin_factors?.[product] ?? 0.25;
  const lotSize = elig?.lot_size ?? 1;
  // Currency of the margin ESTIMATE: it is qty × instrument price × multiplier,
  // so it is denominated in the instrument's quote currency. Null until the
  // eligibility read lands — render a dash rather than a guessed symbol.
  const quoteCcy = elig?.currency ?? null;
  const strike = elig?.strike ?? 0;
  const isOption = strike > 0;   // any instrument carrying a strike is an option (CE/PE)
  // Only ARMED-for-manual accounts are shown/tradeable here — the rest are noise
  // + mis-click surface (an unarmed account is refused server-side anyway). You
  // arm accounts on the Broker Accounts page; a hint below counts the hidden ones.
  const allAccounts = elig?.accounts ?? [];
  const accounts = allAccounts.filter((a) => a.armed);
  const unarmedCount = allAccounts.length - accounts.length;
  // BUY option   → pay the FULL premium (premium × qty, no margin).
  // SELL option  → SPAN-ish margin on the UNDERLYING notional (strike × qty),
  //                not the premium (the premium is collected, not paid).
  // future/equity→ notional (price × qty) × margin %.
  const estFor = (q: number) => {
    if (q <= 0 || refPrice <= 0) {return 0;}
    if (isOption) {
      return isBuy
        ? q * refPrice * mult
        : q * (strike > 0 ? strike : refPrice) * mult * factor;
    }
    return q * refPrice * mult * factor;
  };

  const totalQty = accounts.reduce((s, a) => s + (Number(qtys[a.broker_account_id]) || 0), 0);
  const participating = accounts.filter((a) => (Number(qtys[a.broker_account_id]) || 0) > 0).length;
  const totalEst = accounts.reduce((s, a) => s + estFor(Number(qtys[a.broker_account_id]) || 0), 0);
  // Block the place if ANY participating account's qty isn't a whole lot multiple —
  // the ticket already flags it per row, but the broker would reject the order; gate
  // it here instead of firing a wasted (rejected) round-trip (audit).
  const anyBadLot = accounts.some((a) => {
    const qa = Number(qtys[a.broker_account_id]) || 0;
    return qa > 0 && lotSize > 1 && qa % lotSize !== 0;
  });

  // Place the basket: ONE manual order per participating account. Each is gated
  // server-side by the per-account manual-arm (+ global flag + hard-freeze) → an
  // unarmed account comes back 409 ("refused"), captured per-leg so one refusal
  // doesn't abort the others.
  const qc = useQueryClient();
  const [reason, setReason] = React.useState('');
  const place = useMutation({
    mutationFn: async () => {
      const targets = accounts.filter((a) => (Number(qtys[a.broker_account_id]) || 0) > 0);
      const out: Array<{ name: string; ok: boolean; status: string; detail: string }> = [];
      for (const a of targets) {
        const name = a.display_name ?? a.broker_account_id.slice(0, 8);
        try {
          const res = await placeManualOrder(a.broker_account_id, {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- place button disabled when !instrument
            symbol: instrument!.symbol, side, quantity: Number(qtys[a.broker_account_id]) || 0,
            product_type: product, order_type: orderType,
            reason: reason.trim() || 'discretionary manual order',
            ...(orderType === 'LIMIT' && Number(price) > 0 ? { limit_price: Number(price) } : {}),
            // Paper fills price off the side-aware market ref (ask for BUY / bid
            // for SELL) so a paper market fill matches the margin assumption.
            ...(marketRef > 0 ? { ref_price: marketRef } : {}),
          });
          const r = res.data;
          // A resting/working order (pending/open) is a SUCCESS, not a failure —
          // only rejected/failed/error are real failures. Show avg only on a fill.
          const isFail = ['failed', 'rejected', 'error'].includes((r.status || '').toLowerCase());
          out.push({
            name, ok: !isFail, status: r.status,
            detail: `${r.status}${r.filled_qty ? ' · ' + r.filled_qty : ''}${r.filled_qty && r.avg_price != null ? ' @ ' + r.avg_price : ''}`,
          });
        } catch (e) {
          out.push({ name, ok: false, status: 'failed', detail: e instanceof Error ? e.message : 'failed' });
        }
      }
      return out;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['manual-positions'] }),
  });
  const results = place.data ?? null;

  // Clear the previous fill RESULTS + reason when a fresh ticket opens — otherwise
  // reopening stays stuck on the last "filled" screen and can't place a new order
  // (the form-field reset above doesn't touch the place mutation, which lives here).
  React.useEffect(() => {
    if (open) {
      place.reset();
      setReason('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, instrument?.id, side]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[92vh] max-w-6xl flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>
            <span className={cn('font-mono', isBuy ? 'text-long' : 'text-short')}>{side}</span>{' '}
            <span className="font-mono">{instrument?.symbol}</span>
          </DialogTitle>
          <DialogDescription>
            {instrument?.segment_id}
            {elig?.asset_class ? ' · ' + elig.asset_class : ''}
            {elig && elig.lot_size > 1 ? ' · lot ' + elig.lot_size : ''}
          </DialogDescription>
        </DialogHeader>

        {/* Live quote — prominent. The side's FILL price (ASK for a BUY, BID for
            a SELL) is highlighted because that's what a market order pays AND what
            the est-required margin now uses. */}
        <div className="grid shrink-0 grid-cols-4 gap-px overflow-hidden rounded-md border border-border bg-divider">
          <QuoteStat label="LTP" value={fmt(q?.ltp)} />
          <QuoteStat label="Bid" value={fmt(q?.bid)} highlight={!isBuy} hint={!isBuy ? 'fill' : undefined} />
          <QuoteStat label="Ask" value={fmt(q?.ask)} highlight={isBuy} hint={isBuy ? 'fill' : undefined} />
          <QuoteStat label="Volume" value={fmt(q?.volume)} />
        </div>

        {/* Everything between the fixed quote header and the fixed Buy/Sell
            footer scrolls, so the action buttons stay reachable no matter how
            many accounts are listed. */}
        <div className="mt-3 min-h-0 flex-1 overflow-y-auto pr-1">
        <div className="flex flex-col gap-5 md:flex-row">
          {/* LEFT — eligible accounts */}
          <div className="min-w-0 flex-1 rounded border border-border">
            <div className="sticky top-0 z-10 border-b border-border bg-bg-subtle px-3 py-2 text-2xs font-medium text-fg-subtle">
              Accounts {accounts.length ? '(' + accounts.length + ')' : ''}
            </div>
            {eligQ.isLoading ? (
              <div className="p-3"><Skeleton className="h-20 w-full" /></div>
            ) : accounts.length === 0 ? (
              <div className="px-3 py-8 text-center text-2xs text-fg-muted">
                {unarmedCount > 0 ? (
                  <>
                    No accounts <span className="text-warn">armed</span> for manual trading.{' '}
                    {unarmedCount} eligible account{unarmedCount > 1 ? 's' : ''} — arm{' '}
                    {unarmedCount > 1 ? 'them' : 'it'} on <span className="font-mono text-fg">Broker Accounts</span> to trade here.
                  </>
                ) : (
                  <>No enabled account can trade this segment
                  {elig?.segment_id ? ' (' + elig.segment_id + ')' : ''}.</>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-border bg-bg-subtle">
                  <tr>
                    <ThL>Account</ThL>
                    <ThR>Avail. margin</ThR>
                    <ThR>Qty</ThR>
                    <ThR>Est. req</ThR>
                  </tr>
                </thead>
                <tbody>
                  {accounts.map((a) => (
                    <AccountRow
                      key={a.broker_account_id}
                      account={a}
                      qty={qtys[a.broker_account_id] ?? ''}
                      onQty={(v) => setQtys((p) => ({ ...p, [a.broker_account_id]: v }))}
                      est={estFor(Number(qtys[a.broker_account_id]) || 0)}
                      estCurrency={quoteCcy}
                      lotSize={lotSize}
                    />
                  ))}
                </tbody>
              </table>
              </div>
            )}
            {accounts.length > 0 && unarmedCount > 0 ? (
              <div className="border-t border-border bg-bg-subtle px-3 py-1.5 text-2xs text-fg-muted">
                {unarmedCount} more eligible account{unarmedCount > 1 ? 's' : ''} not armed — arm{' '}
                {unarmedCount > 1 ? 'them' : 'it'} on <span className="font-mono text-fg">Broker Accounts</span> to show here.
              </div>
            ) : null}
            <div className="flex items-center justify-between border-t border-border bg-bg-subtle px-3 py-2 font-mono text-2xs text-fg-subtle">
              <span>{participating} acct · {totalQty} qty</span>
              <span className="inline-flex items-center gap-1">
                est. margin
                {quoteCcy ? <Money amount={totalEst} currency={quoteCcy} /> : '—'}
              </span>
            </div>
          </div>

          {/* RIGHT — order settings */}
          <div className="w-full shrink-0 space-y-4 md:w-72">
            <Field label="Order type">
              <select value={orderType} onChange={(e) => setOrderType(e.target.value as 'MARKET' | 'LIMIT')} className={sel}>
                <option value="MARKET">Market</option>
                <option value="LIMIT">Limit</option>
              </select>
            </Field>
            <Field label="Price">
              <Input
                type="number" value={price} disabled={orderType === 'MARKET'}
                step={elig?.tick_size || 0.05}
                onChange={(e) => setPrice(e.target.value)}
                placeholder={orderType === 'MARKET' ? 'at market' : 'required'}
                className={cn(limitInvalid && 'border-crit')}
              />
              {limitInvalid ? (
                <span className="mt-0.5 block text-2xs text-crit">enter a limit price</span>
              ) : null}
            </Field>
            <Field label="Product">
              <select value={product} onChange={(e) => setProduct(e.target.value as 'NRML' | 'CNC')} className={sel}>
                <option value="NRML">NRML</option>
                <option value="CNC">CNC</option>
              </select>
            </Field>
            <Field label="Validity">
              <select value={validity} onChange={(e) => setValidity(e.target.value as 'DAY' | 'IOC')} className={sel}>
                <option value="DAY">DAY</option>
                <option value="IOC">IOC</option>
              </select>
            </Field>
            <p className="text-2xs text-fg-subtle">
              Available margin is live. Required is an <span className="text-warn">estimate</span> — the broker has no pre-trade margin calc.
              {isOption ? (isBuy ? ' Option buy = full premium × qty.' : ' Option sell ≈ SPAN on strike × qty.') : ''}
            </p>
          </div>
        </div>

        {results ? (
          <div className="mt-1 space-y-1 rounded border border-border bg-surface-inset px-3 py-2 text-2xs">
            {results.map((r) => (
              <div key={r.name} className="flex items-center gap-2 font-mono">
                <Badge tone={_placeTone(r.status, r.ok)} size="sm">{r.status}</Badge>
                <span className="text-fg">{r.name}</span>
                <span className="text-fg-muted">{r.detail}</span>
              </div>
            ))}
          </div>
        ) : (
          <>
            <div className="mt-1 flex items-start gap-2 rounded border border-warn/40 bg-warn-soft px-3 py-2 text-2xs text-warn">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>
                Fires REAL orders on <b>every selected account at once</b> (N× fat-finger). Each
                account must be <b>manual-armed</b> (else it&apos;s refused), with the global LIVE
                switch ON.
              </span>
            </div>
            <label className="mt-2 block">
              <span className="text-2xs font-medium text-fg-subtle">
                Reason (optional — audited)
              </span>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="why are you placing this?"
                rows={2}
              />
            </label>
          </>
        )}
        </div>

        <DialogFooter className="shrink-0">
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            {results ? 'Close' : 'Cancel'}
          </Button>
          {!results ? (
            <Button
              variant={isBuy ? 'success' : 'danger'}
              size="lg"
              className="min-w-36 font-semibold"
              disabled={participating === 0 || place.isPending || !instrument || limitInvalid || anyBadLot}
              onClick={() => place.mutate()}
            >
              {place.isPending ? (
                <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
              ) : (
                <Check className="h-4 w-4" />
              )}
              {' '}{side}{totalQty > 0 ? ' ' + totalQty + ' across ' + participating : ''}
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AccountRow({
  account, qty, onQty, est, estCurrency, lotSize,
}: {
  account: EligibleAccount;
  qty: string;
  onQty: (v: string) => void;
  est: number;
  /** Instrument quote currency — null until eligibility loads. */
  estCurrency: string | null;
  lotSize: number;
}) {
  // Fetch funds ONCE on open (no polling — N polling reads would re-trigger the
  // failing broker login while the session is degraded). Reopen to refresh.
  const fundsQ = useQuery({
    queryKey: ['funds', account.broker_account_id],
    queryFn: ({ signal }) => getFunds(account.broker_account_id, signal),
    staleTime: 30_000,
  });
  const avail = fundsQ.data?.data?.margin_available ?? null;
  // The account's SETTLEMENT currency, straight from its own funds read — not
  // the instrument's. Usually the same, but they are two different facts.
  const acctCcy = fundsQ.data?.data?.currency ?? null;
  const q = Number(qty) || 0;
  // Sufficiency compares an estimate priced in the INSTRUMENT's currency against
  // buying power held in the ACCOUNT's. Comparing across currencies without an FX
  // rate is meaningless, so when they differ we withhold the verdict instead of
  // printing a confident wrong one.
  const comparable = acctCcy != null && estCurrency != null && acctCcy === estCurrency;
  const suff = avail == null || !comparable ? null : est <= avail;
  // F&O / commodity qty MUST be a whole multiple of the lot size.
  const badLot = q > 0 && lotSize > 1 && q % lotSize !== 0;

  return (
    <tr className="border-b border-border last:border-b-0">
      <td className="px-3 py-3">
        <div className="font-medium">{account.display_name ?? account.broker_account_id.slice(0, 8)}</div>
        <div className="font-mono text-2xs text-fg-subtle">{account.broker_type}</div>
      </td>
      <td className="px-3 py-3 text-right font-mono text-sm">
        {fundsQ.isLoading ? '…' : avail == null || acctCcy == null ? (
          <span className="text-fg-subtle">—</span>
        ) : <Money amount={avail} currency={acctCcy} />}
      </td>
      <td className="px-2 py-3 text-right">
        <Input
          type="number" min={0} step={lotSize} value={qty}
          onChange={(e) => onQty(e.target.value)}
          className={cn('h-9 w-28 text-right text-sm', badLot && 'border-crit text-crit')}
          title={lotSize > 1 ? `Lot size ${lotSize} — quantity must be a multiple` : undefined}
        />
        {badLot ? <div className="mt-0.5 text-2xs text-crit">mult. of {lotSize}</div> : null}
      </td>
      <td className="px-3 py-3 text-right font-mono text-sm">
        {q > 0 ? (
          <span className="inline-flex items-center gap-1.5">
            {estCurrency ? <Money amount={est} currency={estCurrency} /> : '—'}
            {suff === null ? null : suff
              ? <Badge tone="success" size="sm">ok</Badge>
              : <Badge tone="crit" size="sm">short</Badge>}
          </span>
        ) : <span className="text-fg-subtle">—</span>}
      </td>
    </tr>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-2xs font-medium text-fg-subtle">{label}</span>
      {children}
    </label>
  );
}
function ThL({ children }: { children: React.ReactNode }) {
  return <th className="px-3 py-2 text-left text-2xs font-medium text-fg-subtle">{children}</th>;
}
function ThR({ children }: { children: React.ReactNode }) {
  return <th className="px-3 py-2 text-right text-2xs font-medium text-fg-subtle">{children}</th>;
}
function _placeTone(status: string, ok: boolean): 'success' | 'warn' | 'crit' | 'info' {
  if (!ok) {return 'crit';}
  const s = (status || '').toLowerCase();
  if (s.includes('fill')) {return s.includes('partial') ? 'info' : 'success';}
  return 'warn';   // pending / open / working / new — placed & resting, not a failure
}

function fmt(v: number | null | undefined): string {
  return v == null ? '—' : v.toLocaleString('en-IN', { maximumFractionDigits: 2 });
}

function QuoteStat({
  label, value, highlight = false, hint,
}: {
  label: string;
  value: string;
  highlight?: boolean;
  hint?: string | undefined;
}) {
  return (
    <div className={cn('bg-surface px-3 py-2.5', highlight && 'bg-accent-soft')}>
      <div className="flex items-baseline gap-1.5">
        <span className="text-2xs font-medium text-fg-subtle">
          {label}
        </span>
        {hint ? (
          <span className="text-2xs text-accent">· {hint}</span>
        ) : null}
      </div>
      <div className={cn('mt-0.5 font-mono text-xl tabular-nums', highlight ? 'text-accent' : 'text-fg')}>
        {value}
      </div>
    </div>
  );
}
